// ConfirmOrderReceipt.jsx
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import AppanCourierLogo from "../../assets/Appan Courier Logo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { pdf } from "@react-pdf/renderer";
import OrderReceiptPDF from "./OrderReceiptPDF";

const ConfirmOrderReceipt = ({
  show,
  handleClose,
  receiptData,
  ewaybillNumber,
}) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  if (!show) return null;

  const formatDateOrder = (dateString) => {
    if (!dateString) return "N/A";

    // Detect format like DD/MM/YYYY
    const parts = dateString.split(/[\/\-\.]/);
    let date;

    if (parts.length === 3) {
      const [day, month, year] = parts.map((p) => parseInt(p, 10));
      // ✅ Create date in local time (not UTC)
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return "Invalid Date";

    // ✅ Add current time to the date
    const now = new Date();
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateForInvoice = (input) => {
    if (!input) return "N/A";

    let date;

    // Case 1: Already a Date object
    if (input instanceof Date) {
      date = input;
    }

    // Case 2: Timestamp (number)
    else if (typeof input === "number") {
      date = new Date(input);
    }

    // Case 3: String (DD-MM-YYYY or DD/MM/YYYY or ISO etc.)
    else if (typeof input === "string") {
      const cleaned = input.trim();

      // Match: DD-MM-YYYY or DD/MM/YYYY with optional time
      const match = cleaned.match(
        /^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/,
      );

      if (match) {
        const [, day, month, year, hh = "00", mm = "00", ss = "00"] = match;

        date = new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hh),
          Number(mm),
          Number(ss),
        );
      } else {
        // fallback
        date = new Date(cleaned);
      }
    }

    // Invalid case
    if (!date || isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = receiptData?.downloadInvoice
    ? formatDateForInvoice
    : formatDateOrder;

  const calculateLineTotal = (item) => {
    const price = parseFloat(item.pricePerUnit) || 0;
    const qty = parseFloat(item.quantity) || 1;
    return (price * qty).toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

  const calculateGrandTotal = () => {
    const total =
      receiptData.productRows?.reduce((sum, item) => {
        const price = parseFloat(item.pricePerUnit) || 0;
        const qty = parseFloat(item.quantity) || 1;
        return sum + price * qty;
      }, 0) || 0;

    return total.toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

  const generatePDF = async () => {
    const printable = document.getElementById("printable-receipt");
    const noPrintElems = printable.querySelectorAll(".no-print");
    noPrintElems.forEach((el) => (el.style.display = "none"));

    const canvas = await html2canvas(printable, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    noPrintElems.forEach((el) => (el.style.display = ""));

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    return pdf;
  };

  const handleDownload = async () => {
    const blob = await pdf(
      <OrderReceiptPDF receiptData={receiptData} formatDate={formatDate} />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt-${receiptData.receiptNumber}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    try {
      const blob = await pdf(
        <OrderReceiptPDF receiptData={receiptData} formatDate={formatDate} />,
      ).toBlob();
      const file = new File(
        [blob],
        `receipt-${receiptData.receiptNumber}.pdf`,
        { type: "application/pdf" },
      );

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Order Receipt",
          text: `Receipt ${receiptData.receiptNumber}`,
          files: [file],
        });
      } else {
        handleDownload();
      }
    } catch (err) {
      console.error("Share failed:", err);
      handleDownload();
    }
  };

  const formattedReceiptDate = receiptData.receiptDate
    ? formatDate(receiptData.receiptDate)
    : "";
  const grandTotal = calculateGrandTotal();

  const isLmbpBusinessType = receiptData?.lmbpBusinessType === "SELLER";

  return createPortal(
    <div
      id="modal-overlay"
      className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-black bg-opacity-50 p-7"
    >
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden;
          }
          #modal-overlay, #modal-overlay * {
            visibility: visible;
          }
          #modal-overlay {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            padding: 1.75rem !important;
            display: flex !important;
            align-items: flex-start !important;
            justify-content: center !important;
            z-index: auto !important;
            min-height: auto !important;
          }
          #printable-receipt {
            position: relative !important;
            max-width: none !important;
            max-height: none !important;
            overflow: visible !important;
            margin: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          .print\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .print\\:flex-row {
            flex-direction: row !important;
          }
        }
      `}</style>
      <div
        id="printable-receipt"
        className="w-full max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-y-auto max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-7 py-6 print:px-4 print:py-3 bg-gradient-to-r from-[rgba(242,107,0,0.10)] to-[rgba(255,255,255,0)]">
          {/* Left section - Logo + company details */}
          <div className="flex items-center gap-5">
            <img
              src={AppanCourierLogo}
              alt="courier-logo"
              className="w-[100px] h-[70px] rounded-xl flex-shrink-0"
            />
            <div className="flex flex-col">
              <div className="font-bold text-gray-900 text-lg">
                {receiptData.companyName}
              </div>
              <div className="text-xs text-gray-500 leading-tight mt-1">
                {receiptData.companyAddress}
                <br />
                {receiptData.companyContact}
              </div>
            </div>
          </div>

          {/* Right section - Buttons */}
          <div className="no-print flex items-center gap-4">
            <button
              onClick={() => window.print()}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold shadow-md hover:bg-orange-600 transition-all duration-300"
            >
              Print
            </button>
            <button
              onClick={handleDownload}
              className="border border-orange-500 text-orange-500 px-6 py-2 rounded-lg font-semibold shadow-md hover:bg-orange-50 transition-all duration-300"
            >
              Download
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-7 py-5 pb-9 print:px-4 print:py-3 print:pb-6">
          <div className="text-orange-500 font-bold mb-3 text-sm">
            Customer Order Receipt
          </div>
          <div className="grid print:grid-cols-2 grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            {/* Left Info Card */}
            <div className="border border-gray-200 p-2.5 px-3 rounded-lg bg-white">
              <div className="grid [grid-template-columns:120px_1fr] gap-y-1 gap-x-7 text-xs items-start">
                <div className="text-gray-500">Receipt No:</div>
                <div className="text-gray-900 font-semibold">
                  C-{receiptData.receiptNumber}
                </div>

                <div className="text-gray-500">Date:</div>
                <div className="text-gray-900 font-semibold">
                  {formattedReceiptDate}
                </div>

                <div className="text-gray-500">Express Type:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.expressType || "N/A"}
                </div>

                <div className="text-gray-500">Consignor Name:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.customerName}
                </div>

                <div className="text-gray-500">Consignor Contact:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.customerContact}
                </div>

                <div className="text-gray-500">Consignor Address:</div>
                <div className="text-xs text-gray-500 break-words max-w-full print:text-[10px]">
                  {receiptData.customerAddress}
                </div>
              </div>
            </div>

            {/* Right Info Card */}
            <div className="border border-gray-200 p-2.5 px-3 rounded-lg bg-white">
              <div
                className="grid gap-y-1 gap-x-7 text-xs items-start"
                style={{ gridTemplateColumns: "120px 1fr" }}
              >
                <div className="text-gray-500">Order ID:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.orderId}
                </div>

                <div className="text-gray-500">Waybill No:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.ourWaybillNumber}
                </div>

                <div className="text-gray-500">Consignee Name:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.consigneeName}
                </div>

                <div className="text-gray-500">Consignee Contact:</div>
                <div className="text-gray-900 font-semibold">
                  {receiptData.consigneeContact}
                </div>

                <div className="text-gray-500">Pickup Address:</div>
                <div className="text-xs text-gray-500 break-words max-w-full print:text-[10px]">
                  {receiptData.pickupLocation}
                </div>

                <div className="text-gray-500">Delivery Address:</div>
                <div className="text-xs text-gray-500 break-words max-w-full print:text-[10px]">
                  {receiptData.deliveryAddress}
                </div>

                <div className="text-gray-500">Return Address:</div>
                <div className="text-xs text-gray-500 break-words max-w-full print:text-[10px]">
                  {receiptData.returnAddress}
                </div>
              </div>
            </div>
          </div>

          {/* Order Details Section */}
          <div className="mt-5 pt-3 print:mt-3 print:pt-2 border-t border-dashed border-gray-200">
            <h4 className="mb-2.5 mt-1.5 print:mb-1.5 print:mt-1 text-orange-500 text-sm font-bold">
              Order Details
            </h4>
            <table className="w-full border-collapse mt-1.5 print:mt-1 text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2.5 px-2.5 print:py-1 print:px-2 bg-white border-b-2 border-gray-200 text-gray-500 font-bold">
                    Item Description
                  </th>
                  <th className="text-center py-2.5 px-2.5 print:py-1 print:px-2 bg-white border-b-2 border-gray-200 text-gray-500 font-bold">
                    Qty
                  </th>
                  <th className="text-right py-2.5 px-2.5 print:py-1 print:px-2 bg-white border-b-2 border-gray-200 text-gray-500 font-bold">
                    Weight
                  </th>
                  <th className="text-right py-2.5 px-2.5 print:py-1 print:px-2 bg-white border-b-2 border-gray-200 text-gray-500 font-bold">
                    Product Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {receiptData.productRows?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="py-3 px-2.5 print:py-2 print:px-2 border-b border-dashed border-gray-200">
                      {item.description}
                    </td>
                    <td className="py-3 px-2.5 print:py-2 print:px-2 border-b border-dashed border-gray-200 text-center">
                      {item.quantity}
                    </td>
                    <td className="py-3 px-2.5 print:py-2 print:px-2 border-b border-dashed border-gray-200 text-right">
                      {item.weight} kg
                    </td>
                    <td className="py-3 px-2.5 print:py-2 print:px-2 border-b border-dashed border-gray-200 text-right font-bold">
                      ₹{calculateLineTotal(item)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payment Details Section */}
          <div className="mt-0 pt-3 print:pt-2">
            <h4 className="mb-2.5 mt-1.5 print:mb-1.5 print:mt-1 text-orange-500 text-sm font-bold">
              Payment Details
            </h4>

            <div className="flex print:flex-row flex-col md:flex-row gap-3 print:gap-2 mt-3.5 print:mt-2">
              {/* Left: Payment Details */}
              <div className="w-full border border-gray-200 rounded-lg p-3 print:p-2 bg-white shadow-sm">
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">Freight Charge:</span>
                  {isLmbpBusinessType ? (
                    <span>₹{receiptData?.amount?.toFixed(2)}</span>
                  ) : (
                    <span></span>
                  )}
                </div>
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">Fuel Charge:</span>
                  {isLmbpBusinessType ? (
                    <span>₹{receiptData?.fuelCharges?.toFixed(2)}</span>
                  ) : (
                    <span></span>
                  )}
                </div>
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">
                    GST[{receiptData?.gstPercent}%]:
                  </span>
                  {isLmbpBusinessType ? (
                    <span>₹{receiptData?.gstAmount?.toFixed(2)}</span>
                  ) : (
                    <span></span>
                  )}
                </div>
              </div>

              {/* Right: Payment Mode & Amount */}
              <div className="w-full border border-gray-200 rounded-lg p-3 print:p-2 bg-white shadow-sm">
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">Weight:</span>
                  {isLmbpBusinessType ? (
                    <span>{receiptData?.weight?.toFixed(2)} kg</span>
                  ) : (
                    <span></span>
                  )}
                </div>
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">COD Amount:</span>
                  {isLmbpBusinessType ? (
                    <span>₹{receiptData?.codAmount?.toFixed(2)}</span>
                  ) : (
                    <span></span>
                  )}
                </div>
                <div className="flex justify-between mb-1.5 text-sm text-gray-500">
                  <span className="font-bold">Total Amount:</span>
                  {isLmbpBusinessType ? (
                    <span>₹{receiptData?.totalAmount?.toFixed(2)}</span>
                  ) : (
                    <span></span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Signatures */}
          <div className="flex gap-6 print:mt-4 mt-6 items-end">
            <div className="flex-1 text-center text-xs text-gray-500">
              <div className="border-t border-black mt-[40px] print:mt-[30px] h-px bg-gray-200 mb-2 mx-auto w-full max-w-xs"></div>
              Authorized Signatory
            </div>
            <div className="flex-1 text-center text-xs text-gray-500">
              <div className="border-t border-black mt-[40px] print:mt-[30px] h-px bg-gray-200 mb-2 mx-auto w-full max-w-xs"></div>
              Customer Signature
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="no-print flex justify-center p-5 pt-0 bg-gradient-to-b from-transparent to-gray-50 gap-2.5">
          <button
            onClick={() => window.print()}
            className="bg-orange-500 text-white px-5 py-2 rounded-lg font-semibold cursor-pointer shadow-lg hover:bg-orange-600 transition-colors duration-300 inline-flex items-center"
          >
            Print
          </button>
          <button
            onClick={handleDownload}
            className="bg-orange-500 text-white px-5 py-2 rounded-lg font-semibold cursor-pointer shadow-lg hover:bg-orange-600 transition-colors duration-300 inline-flex items-center"
          >
            Download
          </button>
          <button
            onClick={handleShare}
            className="bg-orange-500 text-white px-5 py-2 rounded-lg font-semibold cursor-pointer shadow-lg hover:bg-orange-600 transition-colors duration-300 inline-flex items-center"
          >
            Share
          </button>
          <button
            onClick={handleClose}
            className="bg-orange-500 text-white px-5 py-2 rounded-lg font-semibold cursor-pointer shadow-lg hover:bg-orange-600 transition-colors duration-300 inline-flex items-center"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmOrderReceipt;
